import Image from "next/image";
import React, { useState } from "react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import ImageWithLoader from "@/atoms/ImageWithLoader";
import PinIcon from "@/public/icons/pin.svg";
import PropertyShareButton from "@/components/landing-new/PropertySearch/components/shareButton";
import { formatPrice } from "@/utils/formatPrice";
import { MoveRight, CalendarClock as Calendar } from "lucide-react";
import { Props } from "./types";
import { format } from "date-fns";
import { getPropertyLoaderImage, getRibbonSource } from "@/utils/properties/utils";

const PropertyCard = ({ property, searchTerm, infoWindow = false, nearestViewingDate }: Props) => {
  const development = searchTerm || property.neighbourhoods?.[0].slug;
  const isLetProperty = property?.ribbon_type === "let";
  const propertyUrl = `/properties/${
    searchTerm || property.neighbourhoods?.[0].slug || property.district_code
  }/${property.id}`;
  const [selectedImage, setSelectedImage] = React.useState(0);
  const renderCustomIndicator = (
    _clickHandler: any,
    isSelected: boolean,
    index: number,
    label: string,
  ) => {
    return (
      <li
        className={`
            ${isSelected ? "bg-landing-orange" : "bg-grey-90"} 
            w-[6px] h-[6px] rounded-full mx-1 cursor-pointer transition-colors duration-300
          `}
        value={index}
        key={index}
        role="button"
        tabIndex={0}
        aria-label={`${label} ${index + 1}`}
      />
    );
  };

  const cardRef = React.useRef<any>(null);

  const handleArrowMouseEnter = () => {
    if (cardRef.current) {
      cardRef.current.style.opacity = "1";
    }
  };

  const handleArrowMouseLeave = () => {
    if (cardRef.current) {
      cardRef.current.style.opacity = "";
    }
  };
  const images = property?.pictures?.data || [];
  const imageUrl = images[0]?.attributes?.formats?.small?.url || images[0]?.attributes?.url;

  const [pressed, setPressed] = useState(false);

  return (
    <div
      className="rounded-2xl shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 bg-white h-full group flex"
      role="tab"
      id={property.id}
    >
      <a
        href={propertyUrl}
        className=" w-full h-full no-underline flex flex-col"
        style={{ WebkitTapHighlightColor: "transparent" }}
        aria-label={`View details for ${property.short_title}`}
      >
        <div
          ref={cardRef}
          onTouchStart={() => setPressed(true)}
          onTouchEnd={() => setPressed(false)}
          onTouchMove={() => setPressed(false)}
          onMouseEnter={() => setPressed(true)}
          onMouseLeave={() => setPressed(false)}
          className={`flex ${
            pressed ? "opacity-80" : ""
          } bg-white relative rounded-tl-[15px] rounded-tr-[15px] cursor-pointer `}
        >
          {!infoWindow ? (
            <Carousel
              axis="horizontal"
              showThumbs={false}
              showIndicators={true}
              showArrows={true}
              className="w-full aspect-video"
              showStatus={false}
              renderIndicator={renderCustomIndicator}
              selectedItem={selectedImage}
              renderArrowPrev={(_onClickHandler, hasPrev) =>
                hasPrev && (
                  <div className="absolute w-[30px] h-[30px]  top-[calc(50%)] left-5 z-10 cursor-pointer">
                    <div
                      onClick={(e) => {
                        setSelectedImage(selectedImage - 1);
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onMouseEnter={handleArrowMouseEnter}
                      onMouseLeave={handleArrowMouseLeave}
                      className="w-full h-full relative  bg-landing-gray rounded-full opacity-90 hover:opacity-100"
                    >
                      <Image
                        src="/now/prev-arrow.svg"
                        alt={"title"}
                        objectFit="contain"
                        layout="fill"
                      />
                    </div>
                  </div>
                )
              }
              renderArrowNext={(onClickHandler, hasNext) =>
                hasNext && (
                  <div className="absolute w-[30px] h-[30px]  top-[calc(50%)] right-5 z-10 cursor-pointer">
                    <div
                      onClick={(e) => {
                        setSelectedImage(selectedImage + 1);
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      onMouseEnter={handleArrowMouseEnter}
                      onMouseLeave={handleArrowMouseLeave}
                      className="w-full h-full relative bg-landing-gray rounded-full opacity-90 hover:opacity-100"
                      style={{ WebkitTapHighlightColor: "transparent" }}
                    >
                      <Image
                        src="/now/next-arrow.svg"
                        alt={"title"}
                        objectFit="contain"
                        layout="fill"
                      />
                    </div>
                  </div>
                )
              }
            >
              {images.map((property: any, index: number) => (
                <div
                  className="w-full h-full rounded-tl-[15px] rounded-tr-[15px]"
                  style={{ backgroundColor: "white" }}
                  key={property.attributes.url}
                >
                  <div className="w-full relative ">
                    <div className="w-full pb-[66.6667%] relative">
                      <ImageWithLoader
                        src={property.attributes?.url}
                        alt={property.attributes?.alternativeText || "Property image"}
                        className="rounded-tl-[15px] rounded-tr-[15px]"
                        style={{
                          width: "100%",
                          height: "auto",
                        }}
                        layout="fill"
                        objectFit="cover"
                        loading={index === 0 ? "eager" : "lazy"}
                        loaderImage={getPropertyLoaderImage(development)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </Carousel>
          ) : (
            <Image
              src={imageUrl}
              alt={images[0]?.attributes?.name || property.short_title}
              className="rounded-tl-[15px] rounded-tr-[15px]"
              priority
              objectFit="cover"
              style={{ width: "100%", height: "auto" }}
              width={720}
              height={400}
            />
          )}
          {property?.ribbon_type && (
            <div className="absolute w-1/2 h-2/3">
              <Image
                src={getRibbonSource(property.ribbon_type)}
                alt={"Ribbon"}
                layout="fill"
                className="object-contain object-left-top"
              />
            </div>
          )}
          {nearestViewingDate != null && (
            <div className="absolute bg-landing-cherry rounded-full opacity-90 p-1 px-2 right-2 top-2 text-white flex gap-1 align-middle justify-center">
              <p>{format(new Date(nearestViewingDate), "d MMM, hh:mm")}</p>
              <Calendar />
            </div>
          )}
        </div>
        <div className="flex flex-col p-5 gap-[20px] font-sans grow justify-between">
          <div className="flex justify-between">
            <div className=" bg-[linear-gradient(270deg,rgba(243,146,55,0.2)_3.04%,rgba(255,171,0,0.2)_96.96%)]  h-[26px] flex items-center gap-1 px-2 py-1 rounded-[5px]">
              <PinIcon className={`h-[14px] w-[11px] fill-landing-orange`} />
              <p className="font-sans font-semibold text-navy text-xs leading-[18px] line-clamp-1">
                {property?.address}
              </p>
            </div>
            {!infoWindow && !isLetProperty && (
              <div
                className="h-[26px] w-[26px]"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseEnter={handleArrowMouseEnter}
                onMouseLeave={handleArrowMouseLeave}
              >
                <PropertyShareButton listing={property} />
              </div>
            )}
          </div>
          <p className=" text-landing-blue font-semibold text-2xl">{property.short_title}</p>
          {!isLetProperty ? (
            <div className="text-landing-blue text-2xl font-normal leading-[30px]">
              <p>
                <span className="font-semibold">{formatPrice(property.pcm)}</span> pcm fixed for{" "}
                {property.lease_length} years
              </p>
              <p>
                <span className="font-semibold">{formatPrice(property.future_buy_back_price)}</span>{" "}
                buy later price
              </p>
            </div>
          ) : (
            <div className="text-landing-blue text-2xl font-normal leading-[30px]">
              <p>Now let under Keyzy&apos;s two-year</p>
              <p>
                <span className="font-semibold">rent-to-own</span> agreement
              </p>
            </div>
          )}
          <div className="flex justify-between text-2xl text-landing-blue flex-wrap max-w-[400px]">
            <div className="flex items-center">
              <div className="w-[32px] relative h-[20px] ">
                <Image
                  src={"/icons/bedroom.svg"}
                  alt={"Bathroom"}
                  layout="fill"
                  objectFit="contain"
                  className="object-contain"
                  priority
                />
              </div>
              <p>{property.bedrooms} beds</p>
            </div>

            <div className="flex items-center">
              <div className="w-[32px] relative h-[20px] ">
                <Image
                  src={"/icons/bathroom.svg"}
                  alt={"Bathroom"}
                  layout="fill"
                  objectFit="contain"
                  className="object-contain"
                  priority
                />
              </div>
              <p>{property.bathrooms} baths</p>
            </div>

            <div className="flex items-center">
              <div className="w-[32px] relative h-[20px] ">
                <Image
                  src={"/icons/property-area.svg"}
                  alt={"Bathroom"}
                  layout="fill"
                  objectFit="contain"
                  className="object-contain"
                  priority
                />
              </div>
              <p>{property.area} sqft</p>
            </div>
          </div>
          {!isLetProperty ? (
            <div className="flex justify-between items-center ">
              <div className="flex gap-2">
                <p className="text-landing-orange text-lg font-semibold leading-normal">
                  See full details
                </p>
                <MoveRight stroke="#F39237" strokeWidth={1.125} />
              </div>
              <div className="text-right font-semibold text-lg">
                <p className="text-grey-300">Added on</p>
                <p className="text-landing-blue">
                  {" "}
                  {format(new Date(property.added_on), "MMM dd, yyyy")}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 items-center justify-center">
              <p className="text-landing-orange text-lg font-semibold leading-normal">
                See details
              </p>
              <MoveRight stroke="#F39237" strokeWidth={1.125} />
            </div>
          )}
        </div>
      </a>
    </div>
  );
};
export default PropertyCard;
